import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Menu, X, ChevronDown, Send, Play, Volume2, VolumeX, Sprout } from 'lucide-react';

/* ════════════════════════════════════════════════════════════════════
   WORLD OF INSPIYAH — HERO ITERATION LOG
   ────────────────────────────────────────────────────────────────────
   ITERATION 1 (April 2026): TEXT hero on dark bg → click "Watch the
                              Vision" → full-bleed object-cover video
                              (Snake Charmer of Faith). [ORIGINAL IDEAL]
   ITERATION 2 (May 2026):    Replaced bg with static Exhibit A image
                              snapshot → conflicted with coded text.
   ITERATION 3 (May 7, 2026): Removed image; auto-played ISMIS in 1:1
                              contained block → wrong aspect, black bars,
                              played before user "entered". REJECTED.
   ITERATION 4 (May 7, 2026): Restore Iter 1 pattern with ISMIS:
                              clean dark gradient hero, click-to-activate
                              full-bleed object-cover, no autoplay.
   ITERATION 5 (May 7, 2026): HYBRID — "Living Veil → Sacred Threshold →
                              Full Reveal":
                              • 0-7s: "Can you hear it?" intro
                              • 7-12s: Snake Charmer of Faith plays muted
                                at 40% opacity behind text (Living Veil)
                              • 12s+: Snake Charmer freezes on last frame
                                (Sacred Threshold — stillness + reverence)
                              • Click Enter/Watch → ISMIS plays full-bleed
                                full-volume, hero text hides (Full Reveal)
                              [CURRENT — APPROVED PATTERN]
   ════════════════════════════════════════════════════════════════════ */

/* World of INSPIYAH — Portal Landing · Ledger CE-SITE-01
   v1 (SUPERSEDED): original portal — "Ignite Yah Spirit" + Living Veil (eye) video + Enter Portal / Watch Vision
   v2 (ACTIVE): + bookmark ribbon & "Get Your Free Seedz" CTA -> /coming-soon (all v1 visuals/overlay/veil unchanged) */
const KOTF_VIDEO = "https://customer-assets.emergentagent.com/job_0fb4073d-6247-44e8-be18-a96e363bbb90/artifacts/b8k21add_ISMIS.mp4";
// Iteration 6: switched to v3 (5-second clip) for tighter breathing cadence
const SNAKE_CHARMER_VIDEO = "https://customer-assets.emergentagent.com/job_0fb4073d-6247-44e8-be18-a96e363bbb90/artifacts/t8f7sguv_Snake%20Charmer%20Of%20Faith%20Emergent%20Uploadv3.mp4";

// Iteration 5: where in the Snake Charmer clip the snake-charmer figure is most iconic.
// 0.0 = first frame, 1.0 = last frame (eye). Adjust this if the freeze frame needs to shift.
const VEIL_FREEZE_PERCENT = 0.35;
// Iteration 6: heartbeat cadence — pause this long between the two "breaths"
const VEIL_BREATH_HOLD_MS = 1200;
// Iteration 6: fade-to-black mask duration (each direction) when seeking to freeze frame
const VEIL_FADE_MS = 400;

const NAV_LINKS = [
  { label: 'ART Energi', href: 'https://artenergi.org' },
  { label: 'INSPIYAH Culture', href: 'https://www.youtube.com/@INSPIYAHCULTURE' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '#contact' },
];

const SCRIPTURES = [
  { text: "May people know that you, whose name is YHWH יהוה — You alone are the most high over all the earth.", ref: "Psalms 83:18" },
  { text: "Listen for what I say is important, my lips speak what is right. For my mouth softly utters truth.", ref: "Proverbs 8:6,7" },
  { text: "For he will rescue you from the trap of the bird catcher from the destructive pestilence.", ref: "Psalms 91:3" },
  { text: "In that day I will cause a horn to sprout for the house of Israel, and they will have to know that I am YHWH יהוה.", ref: "Ezekiel 29:21" },
];

// Ember particle component
const Embers = () => {
  const embers = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    size: 2 + Math.random() * 3,
    duration: 4 + Math.random() * 6,
    delay: Math.random() * 5,
    opacity: 0.3 + Math.random() * 0.5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-20">
      {embers.map((e) => (
        <motion.div
          key={e.id}
          className="absolute rounded-full"
          style={{
            left: `${e.x}%`,
            bottom: '-10px',
            width: e.size,
            height: e.size,
            background: `radial-gradient(circle, #B87333 0%, #C45B2D 50%, transparent 100%)`,
          }}
          animate={{
            y: [0, -800 - Math.random() * 400],
            x: [0, (Math.random() - 0.5) * 100],
            opacity: [0, e.opacity, e.opacity, 0],
            scale: [0.5, 1, 0.3],
          }}
          transition={{
            duration: e.duration,
            delay: e.delay,
            repeat: Infinity,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

const WorldOfInspiyah = () => {
  const [mobileNav, setMobileNav] = useState(false);
  const [introComplete, setIntroComplete] = useState(false);
  const [introText, setIntroText] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentScripture, setCurrentScripture] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [contactData, setContactData] = useState({ name: '', email: '', message: '' });
  const [pollSelected, setPollSelected] = useState([]);
  // Iteration 6.1: per-pillar witness capture state (email + status per pillar)
  const [witnessState, setWitnessState] = useState({}); // { YAH: { email, sending, sent, error } }
  const videoRef = useRef(null);
  const heroVideoRef = useRef(null);
  // Iteration 5 — Living Veil (motion) → Sacred Threshold (frozen)
  const [veilPaused, setVeilPaused] = useState(false);
  // Iteration 6 — Heartbeat cadence: track which "breath" we're on (0=first, 1=second/final)
  const veilPlayCountRef = useRef(0);
  // Iteration 6 — Fade-to-black overlay during seek-to-freeze transition
  const [veilFadeMask, setVeilFadeMask] = useState(false);

  // Intro sequence — 7 seconds
  useEffect(() => {
    const t1 = setTimeout(() => setIntroText(true), 500);
    const t2 = setTimeout(() => setIntroComplete(true), 7000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Living Veil → Sacred Threshold (Iteration 6 heartbeat cadence):
  // - Reset video to currentTime=0, reset play count, reset state
  // - Play through full clip (breath 1)
  // - onEnded: hold for VEIL_BREATH_HOLD_MS (the "exhale"), then play again (breath 2)
  // - 2nd onEnded: fade-to-black mask, seek to VEIL_FREEZE_PERCENT, fade out, freeze on snake charmer
  // - Removed `autoPlay` so we have full control across browser caches.
  useEffect(() => {
    if (!introComplete) return;
    const v = heroVideoRef.current;
    if (!v) return;

    const startVeil = () => {
      try { v.currentTime = 0; } catch { /* ignore */ }
      veilPlayCountRef.current = 0;
      setVeilPaused(false);
      setVeilFadeMask(false);
      v.play().catch(() => {});
    };

    if (v.readyState >= 1) {
      const t = setTimeout(startVeil, 200);
      return () => clearTimeout(t);
    }
    v.addEventListener('loadedmetadata', startVeil, { once: true });
    return () => v.removeEventListener('loadedmetadata', startVeil);
  }, [introComplete]);

  // Scripture rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScripture(prev => (prev + 1) % SCRIPTURES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const togglePoll = (item) => {
    // Iteration 6.1: single-select — clicking a pillar reveals only its vow,
    // replacing any previously selected vow (no scroll needed)
    setPollSelected(prev => prev.includes(item) ? [] : [item]);
  };

  // Iteration 6.1: send the selected vow to the visitor's email
  const submitWitness = async (pillar) => {
    const current = witnessState[pillar] || {};
    const email = (current.email || '').trim();
    if (!email) return;
    setWitnessState(prev => ({ ...prev, [pillar]: { ...current, sending: true, error: null } }));
    try {
      const res = await fetch('/api/vow/witness', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, pillar }),
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let detail = `Server returned ${res.status}`;
        if (ct.includes('application/json')) {
          const data = await res.json().catch(() => ({}));
          detail = data.detail || detail;
        } else {
          const txt = await res.text().catch(() => '');
          detail = txt ? `${detail}: ${txt.slice(0, 120)}` : detail;
        }
        throw new Error(detail);
      }
      setWitnessState(prev => ({ ...prev, [pillar]: { email: '', sending: false, sent: true, error: null } }));
    } catch (err) {
      // Surface the real error so we can debug routing/CORS/network issues without guessing
      const message = err.message || 'Network error — could not reach server';
      setWitnessState(prev => ({ ...prev, [pillar]: { ...current, sending: false, error: message } }));
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) videoRef.current.muted = !isMuted;
  };

  // Iteration 6: smooth scroll helper for "Enter the Portal" / "Open the Portal"
  const scrollToSection = (selector) => {
    const el = document.querySelector(selector);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Iteration 4: video does NOT autoplay — only on user click ("Watch the Vision")
  const activateVideo = () => {
    setShowVideo(true);
    setTimeout(() => {
      if (videoRef.current) videoRef.current.play().catch(() => {});
    }, 50);
  };

  const closeVideo = () => {
    setShowVideo(false);
    if (videoRef.current) videoRef.current.pause();
  };

  // Iteration 6: combined "close ISMIS + flow to next dimension" gesture
  const openThePortal = () => {
    setShowVideo(false);
    if (videoRef.current) videoRef.current.pause();
    setTimeout(() => scrollToSection('#poll'), 250);
  };

  return (
    <div className="min-h-screen" style={{ background: '#050505', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400&family=Manrope:wght@300;400;500;600&display=swap');
        .woi-sans { font-family: 'Manrope', sans-serif; }
        ::selection { background: rgba(184, 115, 51, 0.4); color: #fff; }
        .grain-overlay {
          position: fixed; inset: 0; z-index: 9999; pointer-events: none; opacity: 0.04;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
        }
        .shimmer { background-size: 200% 100%; animation: shimmer 3s ease-in-out infinite; }
        @keyframes shimmer { 0%,100% { background-position: -200% 0; } 50% { background-position: 200% 0; } }
        .breathe { animation: breathe 6s ease-in-out infinite; }
        @keyframes breathe { 0%,100% { transform: scale(1); } 50% { transform: scale(1.02); } }
        .glow-pulse { animation: glowPulse 3s ease-in-out infinite; }
        @keyframes glowPulse { 0%,100% { text-shadow: 0 0 20px rgba(184,115,51,0.3); } 50% { text-shadow: 0 0 40px rgba(184,115,51,0.6), 0 0 80px rgba(196,91,45,0.3); } }
      `}</style>

      {/* Grain overlay */}
      <div className="grain-overlay" />

      {/* ═══ INTRO SEQUENCE ═══ */}
      <AnimatePresence>
        {!introComplete && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center"
            style={{ background: '#050505' }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={introText ? { opacity: 1 } : {}}
              transition={{ duration: 1.5 }}
              className="text-center"
            >
              <p className="text-2xl lg:text-3xl font-light italic" style={{ color: '#E8D8C8' }}>
                Can you hear it?
              </p>
              <motion.div
                className="mx-auto mt-6 h-px"
                style={{ background: 'linear-gradient(90deg, transparent, #B87333, transparent)' }}
                initial={{ width: 0 }}
                animate={introText ? { width: 200 } : {}}
                transition={{ duration: 2, delay: 0.5 }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ SIDE NAVIGATION ═══ */}
      <nav
        className="fixed left-0 top-0 bottom-0 w-64 z-50 hidden lg:flex flex-col justify-between py-10 px-6"
        style={{ background: 'rgba(5,5,5,0.85)', backdropFilter: 'blur(20px)', borderRight: '1px solid rgba(184,115,51,0.12)' }}
        data-testid="side-navigation"
      >
        <div>
          <div className="mb-12">
            <p className="woi-sans text-xs tracking-[0.3em] uppercase" style={{ color: '#B87333' }}>World Of</p>
            <h2 className="text-2xl font-light text-white mt-1 tracking-wider">INSPIYAH</h2>
            <motion.div
              className="h-px mt-3 shimmer"
              style={{ background: 'linear-gradient(90deg, #B87333, #D4AF37, #B87333)', width: '32px' }}
            />
          </div>
          <div className="space-y-1">
            {NAV_LINKS.map((link) => (
              <a key={link.label} href={link.href}
                className="block py-3 px-3 woi-sans text-sm text-gray-500 hover:text-white transition-all duration-500 relative group">
                <span className="relative z-10">{link.label}</span>
                <motion.div className="absolute left-0 top-0 bottom-0 w-0 group-hover:w-full transition-all duration-500" style={{ background: 'rgba(184,115,51,0.05)' }} />
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-transparent group-hover:bg-[#B87333] transition-all duration-300" />
              </a>
            ))}
          </div>
        </div>
        <div>
          <div className="w-full h-px mb-4" style={{ background: 'rgba(184,115,51,0.1)' }} />
          <p className="woi-sans text-xs text-gray-700 leading-relaxed">
            Divine Intelligence Illuminates<br/>Human Intelligence Leads<br/>AI Intelligence Expands
          </p>
          <p className="woi-sans text-xs mt-3" style={{ color: '#B87333' }}>KOTF888</p>
        </div>
      </nav>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4" style={{ background: 'rgba(5,5,5,0.9)', backdropFilter: 'blur(20px)' }}>
        <h2 className="text-lg font-light text-white tracking-wider">INSPIYAH</h2>
        <button onClick={() => setMobileNav(!mobileNav)} className="text-white"><Menu size={24} /></button>
      </div>

      {mobileNav && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="lg:hidden fixed inset-0 z-[60] flex flex-col items-center justify-center space-y-8" style={{ background: 'rgba(5,5,5,0.97)' }}>
          <button onClick={() => setMobileNav(false)} className="absolute top-5 right-6 text-white"><X size={24} /></button>
          {NAV_LINKS.map((link) => (
            <a key={link.label} href={link.href} onClick={() => setMobileNav(false)} className="text-2xl text-gray-300 hover:text-white transition-colors font-light">{link.label}</a>
          ))}
        </motion.div>
      )}

      {/* ═══ MAIN CONTENT ═══ */}
      <main className="lg:ml-64">

        {/* ═══ HERO — ITERATION 5: HYBRID (Living Veil → Sacred Threshold → Full Reveal) ═══ */}
        <section className="relative min-h-screen overflow-hidden flex flex-col" style={{ minHeight: '100dvh' }} data-testid="hero-section">
          {/* Default dark gradient background */}
          <div className="absolute inset-0 z-0" style={{
            background: 'radial-gradient(ellipse at 50% 30%, rgba(184,115,51,0.18) 0%, rgba(5,5,5,0.6) 35%, #050505 75%)'
          }} />

          {/* ─── LIVING VEIL — Snake Charmer of Faith plays muted at low opacity, freezes after 5s ─── */}
          {!showVideo && (
            <div className="absolute top-0 left-0 right-0 z-[1] pointer-events-none" style={{ height: '100dvh' }} data-testid="living-veil">
              <video
                ref={heroVideoRef}
                className="w-full h-full"
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                  opacity: introComplete ? (veilPaused ? 0.7 : 0.6) : 0,
                  transition: 'opacity 1.6s ease-in-out, filter 1.6s ease-in-out',
                  filter: veilPaused ? 'saturate(1.1) contrast(1.05)' : 'saturate(0.95)',
                }}
                muted
                loop={false}
                playsInline
                preload="auto"
                onEnded={() => {
                  // Iteration 6: heartbeat cadence
                  // - 1st end: hold (exhale), then replay from 0 (breath 2)
                  // - 2nd end: fade-to-black mask, seek to snake-charmer frame, freeze, fade out
                  const v = heroVideoRef.current;
                  if (!v) return;

                  if (veilPlayCountRef.current === 0) {
                    veilPlayCountRef.current = 1;
                    // Hold on the eye briefly (the "exhale"), then breathe in again
                    setTimeout(() => {
                      try { v.currentTime = 0; } catch { /* ignore */ }
                      v.play().catch(() => {});
                    }, VEIL_BREATH_HOLD_MS);
                    return;
                  }

                  // Final breath complete → cinematic fade-to-black masks the rewind seek
                  setVeilFadeMask(true);
                  setTimeout(() => {
                    const dur = v.duration || 5;
                    try {
                      v.currentTime = Math.max(0.05, dur * VEIL_FREEZE_PERCENT);
                    } catch { /* ignore */ }
                    v.pause();
                    setVeilPaused(true);
                    // Fade the mask back out to reveal the snake charmer
                    setTimeout(() => setVeilFadeMask(false), VEIL_FADE_MS);
                  }, VEIL_FADE_MS);
                }}
                data-testid="hero-veil-video"
              >
                <source src={SNAKE_CHARMER_VIDEO} type="video/mp4" />
              </video>
              {/* Radial vignette for text readability — dark at edges/bottom, lighter at center where video shows */}
              <div className="absolute inset-0" style={{
                background: 'radial-gradient(ellipse at 50% 45%, rgba(5,5,5,0.15) 0%, rgba(5,5,5,0.45) 55%, rgba(5,5,5,0.8) 100%)'
              }} />
              {/* Bottom fade for CTA readability */}
              <div className="absolute inset-x-0 bottom-0 h-1/3" style={{
                background: 'linear-gradient(to top, rgba(5,5,5,0.7), transparent)'
              }} />
              {/* Iteration 6: cinematic fade-to-black mask during seek-to-freeze transition */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: '#050505',
                  opacity: veilFadeMask ? 1 : 0,
                  transition: `opacity ${VEIL_FADE_MS}ms ease-in-out`,
                }}
                data-testid="veil-fade-mask"
              />
            </div>
          )}

          {/* ─── FULL REVEAL — ISMIS full-bleed object-cover, only when activated ─── */}
          <AnimatePresence>
            {showVideo && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.2 }}
                className="absolute top-0 left-0 right-0 z-10"
                style={{ height: '100dvh' }}
                data-testid="hero-video-fullbleed"
              >
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain sm:object-cover"
                  style={{ objectPosition: 'center' }}
                  muted={isMuted}
                  loop
                  playsInline
                  preload="auto"
                  data-testid="hero-video"
                >
                  <source src={KOTF_VIDEO} type="video/mp4" />
                </video>
                {/* Readability gradient layered over video for text */}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(5,5,5,0.45) 0%, rgba(5,5,5,0.15) 30%, rgba(5,5,5,0.15) 60%, rgba(5,5,5,0.7) 100%)' }} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Embers (above bg, below text) */}
          <Embers />

          {/* Hero content stack — Phase B (visible when video NOT active). Phase C hides this so video text doesn't conflict. */}
          <AnimatePresence>
            {!showVideo && (
              <motion.div
                key="hero-stack"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="relative z-30 flex-1 w-full px-4 sm:px-6 lg:px-12 pt-24 lg:pt-20 pb-12 flex flex-col items-center justify-center"
                style={{ minHeight: 0 }}
              >
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={introComplete ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="woi-sans text-[10px] sm:text-xs tracking-[0.4em] uppercase mb-6 text-center"
              style={{ color: '#B87333' }}
            >
              Step through the portal
            </motion.p>

            {/* Staggered headline — Ignite Yah Spirit */}
            <motion.h1
              initial={{ opacity: 0 }}
              animate={introComplete ? { opacity: 1 } : {}}
              className="text-center mb-6 leading-[1.05]"
              style={{ letterSpacing: '-0.02em' }}
            >
              {"Ignite".split("").map((char, i) => (
                <motion.span
                  key={`i-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={introComplete ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.4 + i * 0.06 }}
                  className="inline-block text-5xl sm:text-6xl lg:text-7xl font-light text-white"
                  style={{ textShadow: '0 2px 24px rgba(0,0,0,0.7)' }}
                >
                  {char}
                </motion.span>
              ))}
              <span className="inline-block w-3" />
              {"Yah".split("").map((char, i) => (
                <motion.span
                  key={`y-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={introComplete ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.7 + i * 0.08 }}
                  className="inline-block text-5xl sm:text-6xl lg:text-7xl font-light glow-pulse"
                  style={{ color: '#B87333' }}
                >
                  {char}
                </motion.span>
              ))}
              <span className="inline-block w-3" />
              {"Spirit".split("").map((char, i) => (
                <motion.span
                  key={`s-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={introComplete ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 1.0 + i * 0.06 }}
                  className="inline-block text-5xl sm:text-6xl lg:text-7xl font-light text-white"
                  style={{ textShadow: '0 2px 24px rgba(0,0,0,0.7)' }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.h1>

            <motion.div
              initial={{ width: 0 }}
              animate={introComplete ? { width: 80 } : {}}
              transition={{ duration: 1, delay: 1.5 }}
              className="h-px mb-8 shimmer"
              style={{ background: 'linear-gradient(90deg, #B87333, #D4AF37, #C45B2D)' }}
            />

            <motion.p
              initial={{ opacity: 0 }}
              animate={introComplete ? { opacity: 1 } : {}}
              transition={{ duration: 1, delay: 1.8 }}
              className="text-center text-base lg:text-lg text-gray-200 font-light leading-relaxed max-w-2xl mb-6 px-4"
              style={{ textShadow: '0 1px 12px rgba(0,0,0,0.7)' }}
            >
              Welcome to the World of INSPIYAH — a symbol of praise to YAH's name. The embodiment of faith, determination and resilience that all of us hold inside.
            </motion.p>

            <motion.blockquote
              initial={{ opacity: 0, y: 10 }}
              animate={introComplete ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 2.0 }}
              className="text-sm italic mb-10 px-6 py-3 max-w-2xl text-center"
              style={{ borderLeft: '2px solid #B87333', borderRight: '2px solid #B87333', color: '#E8D8C8', textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}
            >
              "The Right of Authenticity, The Idea of Reliability, The Duty of Truth, a portal to success! = A.R.T Energi — Triad of Excellence"
            </motion.blockquote>

            {/* Bookmark scanner ribbon (Option B) → free Seedz on /coming-soon */}
            <motion.a
              href="/coming-soon"
              initial={{ opacity: 0, y: 10 }}
              animate={introComplete ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 2.15 }}
              className="woi-sans inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full text-xs tracking-wide transition-all duration-300 hover:shadow-[0_0_24px_rgba(212,175,55,0.35)]"
              style={{ color: '#f3e6cf', background: 'rgba(212,175,55,0.10)', border: '1px solid rgba(212,175,55,0.5)' }}
              data-testid="cta-bookmark-seedz-ribbon"
            >
              <span>🔖 Just scanned the bookmark?</span>
              <span style={{ color: '#D4AF37', fontWeight: 600 }}>Start with your free Seedz →</span>
            </motion.a>

            {/* CTAs — Enter the Portal · Watch the Vision · Get Your Free Seedz */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={introComplete ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 2.3 }}
              className="flex flex-col sm:flex-row gap-4 mb-6"
            >
              <button
                onClick={() => scrollToSection('#poll')}
                className="woi-sans inline-flex items-center justify-center space-x-2 px-8 py-3.5 text-sm tracking-wider transition-all duration-300 hover:shadow-[0_0_30px_rgba(184,115,51,0.5)]"
                style={{ background: '#B87333', color: '#fff' }}
                data-testid="cta-enter-portal"
              >
                <span>Enter the Portal</span>
                <ArrowRight size={16} />
              </button>
              <button
                onClick={showVideo ? closeVideo : activateVideo}
                className="woi-sans inline-flex items-center justify-center space-x-2 px-8 py-3.5 text-sm tracking-wider border transition-all duration-300 text-gray-200 hover:text-white hover:border-[#B87333]"
                style={{ borderColor: 'rgba(184,115,51,0.4)', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }}
                data-testid="cta-watch-vision"
              >
                <Play size={14} />
                <span>{showVideo ? 'Close the Vision' : 'Watch the Vision'}</span>
              </button>
              <a
                href="/coming-soon"
                className="woi-sans inline-flex items-center justify-center space-x-2 px-8 py-3.5 text-sm tracking-wider transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]"
                style={{ background: 'linear-gradient(135deg,#E8C87A,#D4AF37)', color: '#241a07', fontWeight: 600 }}
                data-testid="cta-get-seedz"
              >
                <Sprout size={16} />
                <span>Get Your Free Seedz</span>
              </a>
            </motion.div>

            {/* Audio toggle removed from inner stack — pinned outside as floating control during video */}

            <motion.p
              initial={{ opacity: 0 }}
              animate={introComplete ? { opacity: 0.5 } : {}}
              transition={{ duration: 1, delay: 2.8 }}
              className="woi-sans text-xs tracking-wider"
              style={{ color: '#888' }}
            >
              worldofinspiyah.com
            </motion.p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ─── Floating video controls — Phase C (only during video playback) ─── */}
          {showVideo && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3"
            >
              <button
                onClick={toggleMute}
                className="woi-sans text-xs px-4 py-2 inline-flex items-center space-x-2 transition-all duration-300"
                style={{
                  background: 'rgba(5,5,5,0.7)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(184,115,51,0.4)',
                  color: '#E8D8C8',
                }}
                data-testid="video-mute-toggle"
              >
                {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                <span className="tracking-wider">{isMuted ? 'UNMUTE THE SIREN' : 'MUTE'}</span>
              </button>
              <button
                onClick={openThePortal}
                className="woi-sans text-xs px-4 py-2 inline-flex items-center space-x-2 transition-all duration-300 hover:shadow-[0_0_30px_rgba(184,115,51,0.5)]"
                style={{
                  background: 'rgba(184,115,51,0.85)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(184,115,51,0.6)',
                  color: '#fff',
                }}
                data-testid="video-close-btn"
              >
                <ArrowRight size={14} />
                <span className="tracking-wider">OPEN THE PORTAL</span>
              </button>
            </motion.div>
          )}

          {!showVideo && (
            <motion.div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30"
              animate={{ y: [0, 8, 0] }}
              transition={{ repeat: Infinity, duration: 2.5 }}
            >
              <ChevronDown size={24} className="text-gray-500" />
            </motion.div>
          )}
        </section>

        {/* ═══ ROTATING SCRIPTURE BANNER ═══ */}
        <section className="py-20 px-6 lg:px-16 text-center relative overflow-hidden" style={{ borderTop: '1px solid rgba(184,115,51,0.08)', borderBottom: '1px solid rgba(184,115,51,0.08)' }}>
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={currentScripture}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl mx-auto"
            >
              <p className="text-xl lg:text-2xl font-light italic leading-relaxed" style={{ color: '#E8D8C8' }}>
                "{SCRIPTURES[currentScripture].text}"
              </p>
              <p className="woi-sans text-sm mt-6" style={{ color: '#B87333' }}>— {SCRIPTURES[currentScripture].ref}</p>
            </motion.blockquote>
          </AnimatePresence>
          {/* Indicator dots */}
          <div className="flex justify-center space-x-2 mt-8">
            {SCRIPTURES.map((_, i) => (
              <div key={i} className="w-1.5 h-1.5 rounded-full transition-all duration-300" style={{ background: i === currentScripture ? '#B87333' : 'rgba(184,115,51,0.2)' }} />
            ))}
          </div>
        </section>

        {/* ═══ YAH HEALTH WEALTH HAPPINESS ═══ */}
        <section id="poll" className="py-24 px-6 lg:px-16" data-testid="poll-section">
          <div className="max-w-4xl mx-auto">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="woi-sans text-xs tracking-[0.3em] uppercase text-center mb-4" style={{ color: '#B87333' }}>
              What is your #1 focus right now?
            </motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl lg:text-5xl font-light text-white text-center mb-4">
              YAH HEALTH WEALTH HAPPINESS
            </motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="woi-sans text-sm text-gray-600 text-center mb-16">
              The Universal Pillars of Success
            </motion.p>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { name: 'YAH', color: '#D4AF37', glow: 'rgba(212,175,55,0.15)', label: 'Spiritual Source', vow: "I vow to honor the Source — to give praise, give thanks, and call upon the name of YHWH יהוה. I draw closer to the One whose magnificent works precede me and whose mysteries are yet to be revealed. I move with reverence, knowing every breath is a gift." },
                { name: 'HEALTH', color: '#4ADE80', glow: 'rgba(74,222,128,0.15)', label: 'The Sacred Vessel', vow: "I vow to steward this body and mind as sacred vessels — embracing meditation, movement, and emotional clarity. I cultivate vitality not as vanity, but as the foundation of every breath I am gifted to take. Renew me now, is a daily Yahffirmation!" },
                { name: 'WEALTH', color: '#B87333', glow: 'rgba(184,115,51,0.15)', label: 'Abundance & Contribution', vow: "I vow to expand my creativity, skills, sharpen my craft, and contribute value that outlives me. I receive divinely authorized abundance with gratitude and stewardship, knowing what flows through me is meant to serve more than myself." },
                { name: 'HAPPINESS', color: '#F472B6', glow: 'rgba(244,114,182,0.15)', label: 'Joy in Purpose', vow: "I vow to live in gratitude — to recognize blessings in the ordinary, from the least to the greatest, to honor joy as a daily practice, and to share light wherever I walk. I choose presence and passion, in The Way, the Truth, The Light. Yah be praised always!" },
              ].map((pillar) => {
                const selected = pollSelected.includes(pillar.name);
                return (
                  <motion.button
                    key={pillar.name}
                    onClick={() => togglePoll(pillar.name)}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="relative py-12 px-4 text-center transition-all duration-500"
                    style={{
                      background: selected ? pillar.glow : '#0A0A0A',
                      border: `1px solid ${selected ? pillar.color : 'rgba(184,115,51,0.1)'}`,
                      boxShadow: selected ? `0 0 40px ${pillar.glow}` : 'none',
                    }}
                    data-testid={`poll-option-${pillar.name.toLowerCase()}`}
                  >
                    <span className="text-2xl lg:text-3xl font-light tracking-wider transition-colors duration-300" style={{ color: selected ? pillar.color : '#444' }}>
                      {pillar.name}
                    </span>
                    <span className="block woi-sans text-[10px] tracking-[0.2em] uppercase mt-2 transition-colors duration-300" style={{ color: selected ? pillar.color : '#333' }}>
                      {pillar.label}
                    </span>
                    {selected && (
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: 24 }}
                        className="absolute bottom-4 left-1/2 -translate-x-1/2 h-0.5"
                        style={{ background: pillar.color }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* ─── PILLAR VOWS — animated reveal beneath the grid when selected ─── */}
            <div className="mt-12 space-y-6" data-testid="pillar-vows">
              <AnimatePresence>
                {[
                  { name: 'YAH', color: '#D4AF37', label: 'Spiritual Source', vow: "I vow to honor the Source — to give praise, give thanks, and call upon the name of YHWH יהוה. I draw closer to the One whose magnificent works precede me and whose mysteries are yet to be revealed. I move with reverence, knowing every breath is a gift." },
                  { name: 'HEALTH', color: '#4ADE80', label: 'The Sacred Vessel', vow: "I vow to steward this body and mind as sacred vessels — embracing meditation, movement, and emotional clarity. I cultivate vitality not as vanity, but as the foundation of every breath I am gifted to take. Renew me now, is a daily Yahffirmation!" },
                  { name: 'WEALTH', color: '#B87333', label: 'Abundance & Contribution', vow: "I vow to expand my creativity, skills, sharpen my craft, and contribute value that outlives me. I receive divinely authorized abundance with gratitude and stewardship, knowing what flows through me is meant to serve more than myself." },
                  { name: 'HAPPINESS', color: '#F472B6', label: 'Joy in Purpose', vow: "I vow to live in gratitude — to recognize blessings in the ordinary, from the least to the greatest, to honor joy as a daily practice, and to share light wherever I walk. I choose presence and passion, in The Way, the Truth, The Light. Yah be praised always!" },
                ].filter(p => pollSelected.includes(p.name)).map((pillar) => (
                  <motion.div
                    key={pillar.name}
                    initial={{ opacity: 0, y: 20, scaleY: 0.95 }}
                    animate={{ opacity: 1, y: 0, scaleY: 1 }}
                    exit={{ opacity: 0, y: -10, scaleY: 0.95 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="relative p-8 lg:p-10"
                    style={{
                      background: 'linear-gradient(135deg, rgba(5,5,5,0.95) 0%, rgba(10,10,10,0.95) 100%)',
                      border: `1px solid ${pillar.color}`,
                      boxShadow: `0 0 40px ${pillar.color}22, inset 0 0 60px rgba(0,0,0,0.5)`,
                      transformOrigin: 'top',
                    }}
                    data-testid={`pillar-vow-${pillar.name.toLowerCase()}`}
                  >
                    {/* Top corner accents */}
                    <div className="absolute top-0 left-0 w-8 h-8" style={{ borderTop: `2px solid ${pillar.color}`, borderLeft: `2px solid ${pillar.color}` }} />
                    <div className="absolute top-0 right-0 w-8 h-8" style={{ borderTop: `2px solid ${pillar.color}`, borderRight: `2px solid ${pillar.color}` }} />
                    <div className="absolute bottom-0 left-0 w-8 h-8" style={{ borderBottom: `2px solid ${pillar.color}`, borderLeft: `2px solid ${pillar.color}` }} />
                    <div className="absolute bottom-0 right-0 w-8 h-8" style={{ borderBottom: `2px solid ${pillar.color}`, borderRight: `2px solid ${pillar.color}` }} />

                    <div className="text-center mb-4">
                      <p className="woi-sans text-xs tracking-[0.4em] uppercase mb-2" style={{ color: pillar.color }}>
                        The Vow of
                      </p>
                      <h3 className="text-3xl lg:text-4xl font-light tracking-wider" style={{ color: pillar.color }}>
                        {pillar.name}
                      </h3>
                      <p className="woi-sans text-[11px] tracking-[0.3em] uppercase mt-2" style={{ color: '#888' }}>
                        {pillar.label}
                      </p>
                    </div>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: 64 }}
                      transition={{ duration: 1, delay: 0.3 }}
                      className="h-px mx-auto mb-6"
                      style={{ background: `linear-gradient(90deg, transparent, ${pillar.color}, transparent)` }}
                    />
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 1, delay: 0.5 }}
                      className="text-base lg:text-lg font-light italic leading-relaxed text-center max-w-2xl mx-auto"
                      style={{ color: '#E8D8C8' }}
                    >
                      "{pillar.vow}"
                    </motion.p>

                    {/* ─── ITERATION 6.1 — WITNESS THIS VOW (email capture) ─── */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.8, delay: 0.9 }}
                      className="mt-10 max-w-md mx-auto"
                      data-testid={`witness-vow-${pillar.name.toLowerCase()}`}
                    >
                      {(witnessState[pillar.name] && witnessState[pillar.name].sent) ? (
                        <div
                          className="text-center py-4 px-6"
                          style={{ border: `1px solid ${pillar.color}55`, background: `${pillar.color}0A` }}
                        >
                          <p className="woi-sans text-xs tracking-[0.3em] uppercase mb-2" style={{ color: pillar.color }}>
                            Witnessed
                          </p>
                          <p className="text-sm italic" style={{ color: '#E8D8C8' }}>
                            Your vow of {pillar.name} has been sent. Return to it often.
                          </p>
                        </div>
                      ) : (
                        <form
                          onSubmit={(e) => { e.preventDefault(); submitWitness(pillar.name); }}
                          className="space-y-3"
                        >
                          <p className="woi-sans text-xs text-center leading-relaxed" style={{ color: '#999' }}>
                            If you'd like a copy of this vow sent to you so you can return to it, share your email.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-2">
                            <input
                              type="email"
                              required
                              placeholder="your@email"
                              value={(witnessState[pillar.name] && witnessState[pillar.name].email) || ''}
                              onChange={(e) => setWitnessState(prev => ({
                                ...prev,
                                [pillar.name]: { ...(prev[pillar.name] || {}), email: e.target.value, error: null }
                              }))}
                              className="woi-sans flex-1 py-2.5 px-3 text-sm bg-transparent text-white placeholder-gray-600 border focus:outline-none transition-colors duration-300"
                              style={{ borderColor: `${pillar.color}40` }}
                              onFocus={(e) => { e.target.style.borderColor = pillar.color; }}
                              onBlur={(e) => { e.target.style.borderColor = `${pillar.color}40`; }}
                              data-testid={`witness-email-${pillar.name.toLowerCase()}`}
                            />
                            <button
                              type="submit"
                              disabled={witnessState[pillar.name] && witnessState[pillar.name].sending}
                              className="woi-sans px-5 py-2.5 text-xs tracking-[0.2em] uppercase transition-all duration-300 disabled:opacity-50"
                              style={{ background: pillar.color, color: '#050505' }}
                              data-testid={`witness-submit-${pillar.name.toLowerCase()}`}
                            >
                              {(witnessState[pillar.name] && witnessState[pillar.name].sending) ? 'Sending...' : 'Witness'}
                            </button>
                          </div>
                          {witnessState[pillar.name] && witnessState[pillar.name].error && (
                            <p className="woi-sans text-xs text-center" style={{ color: '#f87171' }}>
                              {witnessState[pillar.name].error}
                            </p>
                          )}
                        </form>
                      )}
                    </motion.div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* ═══ ANCIENT PRINCIPLES ═══ */}
        <section id="principles" className="py-24 px-6 lg:px-16" style={{ background: '#0A0A0A' }} data-testid="principles-section">
          <div className="max-w-4xl mx-auto">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="woi-sans text-xs tracking-[0.3em] uppercase text-center mb-4" style={{ color: '#D4AF37' }}>Ancient Principles</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl lg:text-4xl font-light text-white text-center mb-16">Sacred Words for the Journey</motion.h2>

            <div className="grid md:grid-cols-2 gap-5">
              {[
                { text: "In that day I will cause a horn to sprout for the house of Israel, and I will give you an opportunity to speak among them: and they will have to know that I am YHWH יהוה.", ref: "Ezekiel 29:21" },
                { text: "So, then, as long as we have the opportunity, let us work what is good toward all, but especially toward those related to us in FAITH.", ref: "Galatians 6:10" },
                { text: "For he will rescue you from the trap of the bird catcher from the destructive pestilence. With his pinions he will cover you and under his wings you will take refuge.", ref: "Psalms 91:1-4" },
                { text: "Whenever you make a vow to YHWH יהוה, do not delay to pay it, for he finds no pleasure in the stupid ones. What you vow pay.", ref: "Ecclesiastes 5:4,5" },
              ].map((scripture, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-8 transition-all duration-500 group hover:shadow-[0_0_30px_rgba(184,115,51,0.08)]"
                  style={{ background: '#050505', border: '1px solid rgba(184,115,51,0.08)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(212,175,55,0.25)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(184,115,51,0.08)'; }}
                >
                  <p className="text-lg font-light italic leading-relaxed mb-4" style={{ color: '#E8D8C8' }}>"{scripture.text}"</p>
                  <p className="woi-sans text-sm" style={{ color: '#B87333' }}>— {scripture.ref}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ COMMUNITY SIGNUP ═══ */}
        <section id="community" className="py-24 px-6 lg:px-16" data-testid="community-section">
          <div className="max-w-2xl mx-auto text-center">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="woi-sans text-xs tracking-[0.3em] uppercase mb-4" style={{ color: '#B87333' }}>Join the Movement</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl lg:text-4xl font-light text-white mb-4">The Opportunity to Connect</motion.h2>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="woi-sans text-sm text-gray-600 mb-2">to the EPIIC Hero in all of us</motion.p>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-gray-500 font-light mb-12">
              Join our global community of YAH lovers, Health builders, Wellness seekers, and Happiness creators.
            </motion.p>
            <form className="space-y-6 text-left" onSubmit={(e) => e.preventDefault()}>
              <div className="grid md:grid-cols-2 gap-6">
                <input type="text" placeholder="Name" required value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                  className="woi-sans w-full py-3 px-0 text-sm text-white placeholder-gray-700 bg-transparent border-0 border-b focus:outline-none focus:ring-0 transition-colors duration-300"
                  style={{ borderBottomColor: 'rgba(184,115,51,0.2)' }}
                  onFocus={(e) => e.target.style.borderBottomColor = '#D4AF37'}
                  onBlur={(e) => e.target.style.borderBottomColor = 'rgba(184,115,51,0.2)'}
                />
                <input type="email" placeholder="Email" required value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  className="woi-sans w-full py-3 px-0 text-sm text-white placeholder-gray-700 bg-transparent border-0 border-b focus:outline-none focus:ring-0 transition-colors duration-300"
                  style={{ borderBottomColor: 'rgba(184,115,51,0.2)' }}
                  onFocus={(e) => e.target.style.borderBottomColor = '#D4AF37'}
                  onBlur={(e) => e.target.style.borderBottomColor = 'rgba(184,115,51,0.2)'}
                />
              </div>
              <button type="submit" className="woi-sans w-full flex items-center justify-center space-x-2 py-4 text-sm tracking-wider transition-all duration-300 hover:shadow-[0_0_30px_rgba(184,115,51,0.3)]" style={{ background: '#B87333', color: '#fff' }}>
                <span>Enter the Portal</span>
                <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </section>

        {/* ═══ CONTACT ═══ */}
        <section id="contact" className="py-24 px-6 lg:px-16" style={{ background: '#0A0A0A', borderTop: '1px solid rgba(184,115,51,0.08)' }} data-testid="contact-section">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-light text-white text-center mb-12">Contact</h2>
            <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
              {['Name', 'Email'].map((field) => (
                <input key={field} type={field === 'Email' ? 'email' : 'text'} placeholder={field} required
                  className="woi-sans w-full py-3 px-0 text-sm text-white placeholder-gray-700 bg-transparent border-0 border-b focus:outline-none focus:ring-0 transition-colors"
                  style={{ borderBottomColor: 'rgba(184,115,51,0.2)' }}
                  onFocus={(e) => e.target.style.borderBottomColor = '#D4AF37'}
                  onBlur={(e) => e.target.style.borderBottomColor = 'rgba(184,115,51,0.2)'}
                />
              ))}
              <textarea placeholder="Message" rows={4}
                className="woi-sans w-full py-3 px-0 text-sm text-white placeholder-gray-700 bg-transparent border-0 border-b focus:outline-none focus:ring-0 resize-none"
                style={{ borderBottomColor: 'rgba(184,115,51,0.2)' }}
                onFocus={(e) => e.target.style.borderBottomColor = '#D4AF37'}
                onBlur={(e) => e.target.style.borderBottomColor = 'rgba(184,115,51,0.2)'}
              />
              <button type="submit" className="woi-sans inline-flex items-center space-x-2 px-8 py-3 text-sm tracking-wider border transition-all duration-300 hover:bg-[#B87333] hover:text-white" style={{ borderColor: 'rgba(184,115,51,0.25)', color: '#B87333' }}>
                <Send size={14} />
                <span>Send</span>
              </button>
            </form>
            <div className="grid md:grid-cols-3 gap-8 mt-16 pt-12" style={{ borderTop: '1px solid rgba(184,115,51,0.08)' }}>
              {[
                { label: 'Location', value: 'Park Offices Drive\nNC 27709' },
                { label: 'Hours', value: 'Monday — Friday\n8am — 6pm' },
                { label: 'Contact', value: '184 INSPIYAH\nsupport@inspiyah.com' },
              ].map((item) => (
                <div key={item.label}>
                  <p className="woi-sans text-xs tracking-wider uppercase text-gray-700 mb-2">{item.label}</p>
                  <p className="text-sm text-gray-500 whitespace-pre-line">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ FOOTER ═══ */}
        <footer className="py-12 px-6 lg:px-16 text-center" style={{ borderTop: '1px solid rgba(184,115,51,0.08)' }} data-testid="woi-footer">
          <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mb-4 woi-sans text-xs" aria-label="Legal">
            <a href="/privacy-policy" className="transition-colors" style={{ color: '#B87333' }} data-testid="woi-legal-privacy">Privacy Policy</a>
            <span className="text-gray-700">&bull;</span>
            <a href="/data-processing-addendum" className="transition-colors" style={{ color: '#B87333' }} data-testid="woi-legal-dpa">DPA</a>
            <span className="text-gray-700">&bull;</span>
            <a href="/terms-of-service" className="transition-colors" style={{ color: '#B87333' }} data-testid="woi-legal-terms">Terms of Service</a>
            <span className="text-gray-700">&bull;</span>
            <a href="/ai-usage-policy" className="transition-colors" style={{ color: '#B87333' }} data-testid="woi-legal-ai">AI Usage Policy</a>
            <span className="text-gray-700">&bull;</span>
            <a href="/disclaimer" className="transition-colors" style={{ color: '#B87333' }} data-testid="woi-legal-disclaimer">Disclaimer</a>
            <span className="text-gray-700">&bull;</span>
            <a href="/faq" className="transition-colors" style={{ color: '#B87333' }} data-testid="woi-legal-faq">FAQ</a>
          </nav>
          <p className="woi-sans text-xs text-gray-600">&copy; {new Date().getFullYear()} WOI. All rights reserved.</p>
          <p className="woi-sans text-xs mt-1" style={{ color: '#B87333' }}>A.R.T. Energi (Authenticity | Reliability | Truth) — Triad Of Excellence</p>
          <p className="woi-sans text-xs text-gray-800 mt-2">YAHFFIRMATIONS BY KOTF888</p>
        </footer>
      </main>
    </div>
  );
};

export default WorldOfInspiyah;
